(() => {
  const normalise = (value = '') => value.toString().trim().toLowerCase();

  class MultimediaCentre {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-mmc-grid]');
      this.search = root.querySelector('[data-mmc-search]');
      this.license = root.querySelector('[data-mmc-license]');
      this.count = root.querySelector('[data-mmc-count]');
      this.totalProducts = Number(this.count?.dataset.totalProducts || 0);
      this.empty = root.querySelector('[data-mmc-empty]');
      this.pagination = root.querySelector('[data-mmc-pagination]');
      this.loadingText = root.querySelector('[data-mmc-loading]');
      this.modal = root.querySelector('[data-mmc-modal]');
      this.productGridContainer = root.querySelector('#ProductGridContainer');
      this.productProvider = root.mmcProductProvider || null;
      this.allCards = this.grid ? [...this.grid.querySelectorAll('[data-mmc-card]')] : [];
      this.cardFilterCache = new WeakMap();
      this.displayLimit = 250;
      this.filteredCards = [];
      this.customFilters = root.querySelector('[data-mmc-custom-filters]');
      this.activeFilters = new Map();
      this.filterDefinitions = [
        ['gender', 'Gender'],
        ['audience', 'Audience'],
        ['productType', 'Product type'],
        ['theme', 'Theme'],
        ['character', 'Character'],
        ['brand', 'Brand'],
        ['license', 'License'],
        ['franchise', 'Franchise']
      ];
      this.isLoading = false;
      this.loadPromise = null;
      this.selectedProducts = new Map();

      if (!this.grid) return;

      this.bind();
      this.observeThemeFacetUpdates();
      this.buildCustomFilters();
      this.refreshLicenseOptions();
      this.applyFilters();
    }

    observeThemeFacetUpdates() {
      if (!this.productGridContainer || typeof MutationObserver === 'undefined') return;

      this.gridObserver = new MutationObserver(() => {
        const nextGrid = this.root.querySelector('[data-mmc-grid]');
        if (!nextGrid || nextGrid === this.grid) return;
        this.grid = nextGrid;
        this.allCards = [...nextGrid.querySelectorAll('[data-mmc-card]')];
        this.cardFilterCache = new WeakMap();
        this.displayLimit = 250;
        this.pagination = this.root.querySelector('[data-mmc-pagination]');
        this.empty = this.root.querySelector('[data-mmc-empty]');
        this.refreshLicenseOptions();
        this.restoreVisibleSelections();
        this.applyFilters();
      });

      this.gridObserver.observe(this.productGridContainer, { childList: true, subtree: true });
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-mmc-open]');
        if (openButton) {
          this.openModal(openButton.closest('[data-mmc-card]'));
          return;
        }

        const loadMore = event.target.closest('[data-mmc-load-more]');
        if (loadMore) {
          event.preventDefault();
          this.loadNextPage();
        }

        const showMore = event.target.closest('[data-mmc-show-more]');
        if (showMore) {
          this.displayLimit += 250;
          this.renderFilteredGrid();
          return;
        }

        if (event.target.closest('[data-mmc-close]')) this.closeModal();

        if (event.target.closest('[data-mmc-clear-selection]')) {
          this.clearSelection();
          return;
        }

        const downloadSelected = event.target.closest('[data-mmc-download-selected]');
        if (downloadSelected) {
          this.downloadSelectedProducts(downloadSelected);
          return;
        }

        const downloadFiltered = event.target.closest('[data-mmc-download-filtered]');
        if (downloadFiltered) {
          this.prepareFilteredDownload(downloadFiltered);
          return;
        }

        const directDownload = event.target.closest('[data-mmc-direct-download]');
        if (directDownload) {
          this.openPreparedDownloadConfirmation(directDownload);
          return;
        }

        if (event.target.closest('[data-mmc-confirm-cancel]')) {
          this.closeCollectionDownloadConfirmation();
          return;
        }

        if (event.target.closest('[data-mmc-confirm-continue]')) {
          const filteredProducts = this.pendingFilteredProducts;
          const filteredButton = this.pendingFilteredButton;
          const archiveName = this.pendingFilteredArchiveName;
          const directDownload = this.pendingDirectDownload;
          this.closeCollectionDownloadConfirmation();
          if (directDownload) this.startPreparedDownload(directDownload);
          else if (filteredProducts && filteredButton) this.downloadFilteredProducts(filteredButton, filteredProducts, archiveName);
          return;
        }

        const downloadAll = event.target.closest('[data-mmc-download-all]');
        if (downloadAll) {
          this.downloadAllImages(downloadAll);
          return;
        }

        const download = event.target.closest('[data-mmc-download]');
        if (download) this.downloadImage(download);
      });

      this.root.addEventListener('change', (event) => {
        const checkbox = event.target.closest('[data-mmc-select]');
        if (checkbox) this.updateProductSelection(checkbox.closest('[data-mmc-card]'), checkbox.checked);

        const filterInput = event.target.closest('[data-mmc-filter-input]');
        if (filterInput) {
          this.setFilterValue(filterInput.dataset.filterKey, filterInput.value, filterInput.checked);
        }
      });

      this.root.addEventListener('click', (event) => {
        const disabledFilter = event.target.closest('[data-mmc-custom-filter][aria-disabled="true"]');
        if (disabledFilter) {
          event.preventDefault();
          disabledFilter.removeAttribute('open');
          return;
        }
        const clearAll = event.target.closest('[data-mmc-clear-filters]');
        if (clearAll) this.clearCustomFilters();

        const remove = event.target.closest('[data-mmc-remove-filter]');
        if (remove) this.removeCustomFilter(remove.dataset.filterKey, remove.dataset.filterValue);
      });

      this.root.addEventListener('mmc:products-replaced', (event) => {
        this.grid = this.root.querySelector('[data-mmc-grid]');
        this.allCards = this.grid ? [...this.grid.querySelectorAll('[data-mmc-card]')] : [];
        this.cardFilterCache = new WeakMap();
        this.displayLimit = 250;
        this.pagination = this.root.querySelector('[data-mmc-pagination]');
        this.empty = this.root.querySelector('[data-mmc-empty]');
        this.count = this.root.querySelector('[data-mmc-count]');
        this.totalProducts = Number(event.detail?.totalProducts || this.count?.dataset.totalProducts || 0);
        this.clearSelection();
        this.refreshLicenseOptions();
        this.applyFilters();
      });

      this.search?.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(async () => {
          if (normalise(this.search.value).length > 0) await this.loadAllPages();
          this.displayLimit = 250;
          this.applyFilters();
        }, 250);
      });

      this.license?.addEventListener('change', async () => {
        if (this.license.value) await this.loadAllPages();
        this.applyFilters();
      });

      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });

      this.modal?.addEventListener('close', () => {
        document.body.classList.remove('mmc-modal-open');
      });

      this.collectionConfirm = this.root.querySelector('[data-mmc-collection-confirm]');
      this.collectionConfirm?.addEventListener('click', (event) => {
        if (event.target === this.collectionConfirm) this.closeCollectionDownloadConfirmation();
      });
      this.collectionConfirm?.addEventListener('close', () => {
        this.pendingCollectionDownloadButton = null;
        this.pendingFilteredProducts = null;
        this.pendingFilteredButton = null;
        this.pendingFilteredArchiveName = null;
        this.pendingDirectDownload = null;
      });

      document.addEventListener('click', (event) => {
        const current = this.root.contains(event.target) ? event.target.closest('[data-mmc-custom-filter]') : null;
        this.customFilters?.querySelectorAll('[data-mmc-custom-filter][open]').forEach((details) => {
          if (details !== current) details.removeAttribute('open');
        });
      });
    }

    cards() {
      return this.allCards;
    }

    async updateProductSelection(card, selected) {
      if (!card) return;
      const key = card.dataset.selectionKey;
      if (!key) return;

      if (selected) {
        if (this.productProvider?.hydrateCard && !card.querySelector('[data-mmc-image]')) await this.productProvider.hydrateCard(card);
        const product = this.readProductDownloadData(card);
        if (product) this.selectedProducts.set(key, product);
      } else {
        this.selectedProducts.delete(key);
      }

      this.updateSelectionBar();
    }

    readProductDownloadData(card) {
      const template = card.querySelector('[data-mmc-product-data]');
      const data = template?.content.firstElementChild;
      if (!data) return null;

      return {
        key: card.dataset.selectionKey,
        title: data.dataset.productTitle || card.dataset.title || 'Product',
        code: data.dataset.productCode || card.dataset.styleCode || card.dataset.selectionKey,
        images: [...data.querySelectorAll('[data-mmc-image]')].map((image) => ({
          url: image.dataset.download,
          filename: image.dataset.filename
        }))
      };
    }

    restoreVisibleSelections() {
      this.grid.querySelectorAll('[data-mmc-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-mmc-select]');
        if (checkbox) checkbox.checked = this.selectedProducts.has(card.dataset.selectionKey);
      });
      this.updateSelectionBar();
    }

    updateSelectionBar() {
      const bar = this.root.querySelector('[data-mmc-selection-bar]');
      if (!bar) return;

      const productCount = this.selectedProducts.size;
      const imageCount = [...this.selectedProducts.values()]
        .reduce((total, product) => total + product.images.length, 0);
      const count = bar.querySelector('[data-mmc-selection-count]');
      const images = bar.querySelector('[data-mmc-selection-images]');
      const status = bar.querySelector('[data-mmc-selection-status]');

      bar.hidden = productCount === 0;
      if (count) count.textContent = `${productCount} ${productCount === 1 ? 'product' : 'products'} selected`;
      if (images) images.textContent = `${imageCount} ${imageCount === 1 ? 'image' : 'images'} ready to download`;
      if (status && productCount === 0) status.textContent = '';
    }

    clearSelection() {
      this.selectedProducts.clear();
      this.grid.querySelectorAll('[data-mmc-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-mmc-select]');
        if (checkbox) checkbox.checked = false;
      });
      const status = this.root.querySelector('[data-mmc-selection-status]');
      if (status) status.textContent = '';
      this.updateSelectionBar();
    }

    applyFilters() {
      const query = normalise(this.search?.value);
      const selectedLicense = normalise(this.license?.value);
      this.filteredCards = this.cards().filter((card) => {
        const haystack = normalise([
          card.dataset.title,
          card.dataset.handle,
          card.dataset.styleCode,
          card.dataset.skus,
          card.dataset.license,
          ...this.filterDefinitions.flatMap(([key]) => this.cardFilterValues(card, key))
        ].join(' '));
        const matchesSearch = !query || query.split(/\s+/).every((term) => haystack.includes(term));
        const matchesLicense = !selectedLicense || normalise(card.dataset.license) === selectedLicense;
        const matchesCustomFilters = this.matchesCustomFilters(card);
        const show = matchesSearch && matchesLicense && matchesCustomFilters;
        card.hidden = false;
        return show;
      });
      this.renderFilteredGrid();
      this.updateCustomFilterControls();
    }

    renderFilteredGrid() {
      if (!this.grid) return;
      const shownCards = this.filteredCards.slice(0, this.displayLimit);
      const fragment = document.createDocumentFragment();
      shownCards.forEach((card) => fragment.append(card));
      this.grid.replaceChildren(fragment);

      if (this.count) {
        const shown = shownCards.length;
        const matches = this.filteredCards.length;
        const suffix = matches === 1 ? 'product' : 'products';
        this.count.textContent = `Showing ${shown} of ${matches} ${suffix}`;
      }
      if (this.empty) this.empty.hidden = this.filteredCards.length !== 0;

      if (this.pagination) {
        this.pagination.replaceChildren();
        if (shownCards.length < this.filteredCards.length) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'button mmc__load-more';
          button.dataset.mmcShowMore = '';
          button.textContent = `Show 250 more products`;
          this.pagination.append(button);
        }
      }
      this.restoreVisibleSelections();
    }

    cardFilterValues(card, key) {
      let cached = this.cardFilterCache.get(card);
      if (!cached) {
        cached = new Map();
        this.cardFilterCache.set(card, cached);
      }
      if (cached.has(key)) return cached.get(key);
      const values = (card.dataset[`filter${key.charAt(0).toUpperCase()}${key.slice(1)}`] || '')
        .split('||')
        .map((value) => value.trim())
        .filter(Boolean);
      cached.set(key, values);
      return values;
    }

    matchesCustomFilters(card, excludedKey = '') {
      if (!this.matchesVariantFilters(card, excludedKey)) return false;
      return this.filterDefinitions.every(([key]) => {
        if (key === 'gender' || key === 'audience') return true;
        if (key === excludedKey) return true;
        const selected = this.activeFilters.get(key);
        if (!selected?.size) return true;
        return this.cardFilterValues(card, key).some((value) => selected.has(normalise(value)));
      });
    }

    cardVariantFilters(card) {
      const cacheKey = '__variantFilters';
      let cached = this.cardFilterCache.get(card);
      if (!cached) {
        cached = new Map();
        this.cardFilterCache.set(card, cached);
      }
      if (cached.has(cacheKey)) return cached.get(cacheKey);
      let variants = [];
      try { variants = JSON.parse(card.dataset.filterVariantPairs || '[]'); } catch (_) { variants = []; }
      if (!variants.length) {
        variants = [{ gender: this.cardFilterValues(card, 'gender'), audience: this.cardFilterValues(card, 'audience') }];
      }
      cached.set(cacheKey, variants);
      return variants;
    }

    matchesVariantFilters(card, excludedKey = '') {
      const genders = excludedKey === 'gender' ? null : this.activeFilters.get('gender');
      const audiences = excludedKey === 'audience' ? null : this.activeFilters.get('audience');
      if (!genders?.size && !audiences?.size) return true;
      return this.cardVariantFilters(card).some((variant) => {
        const matchesGender = !genders?.size || (variant.gender || []).some((value) => genders.has(normalise(value)));
        const matchesAudience = !audiences?.size || (variant.audience || []).some((value) => audiences.has(normalise(value)));
        return matchesGender && matchesAudience;
      });
    }

    availableFilterValues(card, key) {
      if (key !== 'gender' && key !== 'audience') return this.cardFilterValues(card, key);
      const otherKey = key === 'gender' ? 'audience' : 'gender';
      const otherSelected = this.activeFilters.get(otherKey);
      if (!otherSelected?.size) return this.cardFilterValues(card, key);
      return [...new Set(this.cardVariantFilters(card)
        .filter((variant) => (variant[otherKey] || []).some((value) => otherSelected.has(normalise(value))))
        .flatMap((variant) => variant[key] || []))];
    }

    buildCustomFilters() {
      if (!this.customFilters) return;
      const groups = this.customFilters.querySelector('[data-mmc-filter-groups]');
      if (!groups) return;
      groups.replaceChildren();

      this.filterDefinitions.forEach(([key, label]) => {
        const valueCounts = new Map();
        this.cards().forEach((card) => {
          new Set(this.cardFilterValues(card, key)).forEach((value) => {
            valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
          });
        });
        const values = [...valueCounts.keys()].sort((a, b) => a.localeCompare(b));
        if (!values.length) return;

        const details = document.createElement('details');
        details.className = 'mmc-filter';
        details.dataset.mmcCustomFilter = key;
        const summary = document.createElement('summary');
        summary.className = 'mmc-filter__summary';
        const title = document.createElement('span');
        title.textContent = label;
        const badge = document.createElement('span');
        badge.className = 'mmc-filter__badge';
        badge.dataset.mmcFilterBadge = '';
        badge.hidden = true;
        const chevron = document.createElement('span');
        chevron.className = 'mmc-filter__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        summary.append(title, badge, chevron);

        const panel = document.createElement('div');
        panel.className = 'mmc-filter__panel';
        const list = document.createElement('ul');
        list.className = 'mmc-filter__values';
        list.setAttribute('role', 'list');
        values.forEach((value) => {
          const item = document.createElement('li');
          const option = document.createElement('label');
          option.className = 'mmc-filter__value';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.value = value;
          input.dataset.mmcFilterInput = '';
          input.dataset.filterKey = key;
          const text = document.createElement('span');
          text.textContent = value;
          const count = document.createElement('span');
          count.className = 'mmc-filter__count';
          count.dataset.mmcFilterCount = '';
          count.textContent = valueCounts.get(value) || 0;
          option.append(input, text, count);
          item.append(option);
          list.append(item);
        });
        panel.append(list);
        details.append(summary, panel);
        groups.append(details);
      });
    }

    setFilterValue(key, value, selected) {
      const values = this.activeFilters.get(key) || new Set();
      if (selected) values.add(normalise(value));
      else values.delete(normalise(value));
      if (values.size) this.activeFilters.set(key, values);
      else this.activeFilters.delete(key);
      this.displayLimit = 250;
      this.applyFilters();
    }

    clearCustomFilters() {
      this.activeFilters.clear();
      this.customFilters?.querySelectorAll('[data-mmc-filter-input]').forEach((input) => {
        input.checked = false;
      });
      this.displayLimit = 250;
      this.applyFilters();
    }

    removeCustomFilter(key, value) {
      const values = this.activeFilters.get(key);
      values?.delete(normalise(value));
      if (!values?.size) this.activeFilters.delete(key);
      this.customFilters?.querySelectorAll(`[data-mmc-filter-input][data-filter-key="${key}"]`).forEach((input) => {
        if (normalise(input.value) === normalise(value)) input.checked = false;
      });
      this.displayLimit = 250;
      this.applyFilters();
    }

    updateCustomFilterControls() {
      if (!this.customFilters) return;
      const query = normalise(this.search?.value);
      this.filterDefinitions.forEach(([key]) => {
        const counts = new Map();
        this.cards().forEach((card) => {
          const haystack = normalise([card.dataset.title, card.dataset.handle, card.dataset.styleCode, card.dataset.skus, ...this.filterDefinitions.flatMap(([filterKey]) => this.cardFilterValues(card, filterKey))].join(' '));
          if (query && !query.split(/\s+/).every((term) => haystack.includes(term))) return;
          if (!this.matchesCustomFilters(card, key)) return;
          new Set(this.availableFilterValues(card, key).map(normalise)).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
        });
        const details = this.customFilters.querySelector(`[data-mmc-custom-filter="${key}"]`);
        details?.querySelectorAll('[data-mmc-filter-input]').forEach((input) => {
          const count = counts.get(normalise(input.value)) || 0;
          const countElement = input.closest('.mmc-filter__value')?.querySelector('[data-mmc-filter-count]');
          if (countElement) countElement.textContent = count;
          input.disabled = count === 0 && !input.checked;
        });
        if (details) {
          const hasOption = [...details.querySelectorAll('[data-mmc-filter-input]')].some((input) => !input.disabled || input.checked);
          details.setAttribute('aria-disabled', String(!hasOption));
          if (!hasOption) details.removeAttribute('open');
        }
      });
      this.customFilters.querySelectorAll('[data-mmc-custom-filter]').forEach((details) => {
        const selectedCount = this.activeFilters.get(details.dataset.mmcCustomFilter)?.size || 0;
        const badge = details.querySelector('[data-mmc-filter-badge]');
        if (badge) {
          badge.textContent = selectedCount;
          badge.hidden = selectedCount === 0;
        }
      });

      const active = this.customFilters.querySelector('[data-mmc-active-filters]');
      if (active) {
        active.replaceChildren();
        this.filterDefinitions.forEach(([key, label]) => {
          this.activeFilters.get(key)?.forEach((value) => {
            const original = this.cards().flatMap((card) => this.cardFilterValues(card, key)).find((item) => normalise(item) === value) || value;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'mmc-filter-pill';
            button.dataset.mmcRemoveFilter = '';
            button.dataset.filterKey = key;
            button.dataset.filterValue = original;
            button.textContent = `${label}: ${original} ×`;
            active.append(button);
          });
        });
      }

      const clear = this.customFilters.querySelector('[data-mmc-clear-filters]');
      if (clear) clear.hidden = this.activeFilters.size === 0;
    }

    refreshLicenseOptions() {
      if (!this.license) return;
      const current = this.license.value;
      const licenses = [...new Set(this.cards()
        .map((card) => card.dataset.license?.trim())
        .filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

      this.license.querySelectorAll('option:not(:first-child)').forEach((option) => option.remove());
      licenses.forEach((license) => {
        const option = document.createElement('option');
        option.value = license;
        option.textContent = license;
        this.license.append(option);
      });
      if ([...this.license.options].some((option) => option.value === current)) this.license.value = current;
    }

    async loadNextPage() {
      if (this.isLoading) return this.loadPromise;
      const link = this.pagination?.querySelector('[data-mmc-load-more]');
      if (!link) return null;

      this.isLoading = true;
      if (this.loadingText) this.loadingText.hidden = false;
      link.setAttribute('aria-disabled', 'true');

      let loadedPage = false;
      this.loadPromise = fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load products (${response.status})`);
          return response.text();
        })
        .then((html) => {
          const page = new DOMParser().parseFromString(html, 'text/html');
          const incomingRoot = page.querySelector(`[data-mmc][data-section-id="${this.root.dataset.sectionId}"]`) || page.querySelector('[data-mmc]');
          const incomingGrid = incomingRoot?.querySelector('[data-mmc-grid]');
          const incomingPagination = incomingRoot?.querySelector('[data-mmc-pagination]');

          if (!incomingGrid) throw new Error('The next Multimedia Centre page could not be read.');
          [...incomingGrid.children].forEach((card) => this.grid.append(card));
          this.pagination.innerHTML = incomingPagination?.innerHTML || '';
          this.refreshLicenseOptions();
          this.restoreVisibleSelections();
          this.applyFilters();
          loadedPage = true;
        })
        .catch((error) => {
          console.error('[Multimedia Centre]', error);
          link.removeAttribute('aria-disabled');
        })
        .finally(() => {
          this.isLoading = false;
          this.loadPromise = null;
          if (this.loadingText) this.loadingText.hidden = true;
        })
        .then(() => loadedPage);

      return this.loadPromise;
    }

    async loadAllPages() {
      while (this.pagination?.querySelector('[data-mmc-load-more]')) {
        const loaded = await this.loadNextPage();
        if (!loaded) break;
      }
    }

    async openModal(card) {
      if (!card || !this.modal) return;
      if (this.productProvider?.hydrateCard && !card.querySelector('[data-mmc-image]')) await this.productProvider.hydrateCard(card);
      const template = card.querySelector('[data-mmc-product-data]');
      if (!template) return;

      const data = template.content.firstElementChild;
      const title = this.modal.querySelector('[data-mmc-modal-title]');
      const code = this.modal.querySelector('[data-mmc-modal-code]');
      const license = this.modal.querySelector('[data-mmc-modal-license]');
      const imageGrid = this.modal.querySelector('[data-mmc-modal-images]');

      title.textContent = data.dataset.productTitle || '';
      code.textContent = data.dataset.productCode ? `Style: ${data.dataset.productCode}` : '';
      license.textContent = data.dataset.productLicense || '';
      imageGrid.replaceChildren();
      this.modal.dataset.zipName = this.safeFilename(data.dataset.productCode || data.dataset.productTitle || 'product-images');

      const downloadStatus = this.modal.querySelector('[data-mmc-download-status]');
      const downloadAll = this.modal.querySelector('[data-mmc-download-all]');
      if (downloadStatus) downloadStatus.textContent = '';
      if (downloadAll) {
        downloadAll.disabled = false;
        downloadAll.textContent = 'Download all images (.zip)';
      }

      data.querySelectorAll('[data-mmc-image]').forEach((image, index) => {
        const item = document.createElement('article');
        item.className = 'mmc-modal__image-card';

        const wrap = document.createElement('div');
        wrap.className = 'mmc-modal__image-wrap';

        const badge = document.createElement('span');
        badge.className = 'mmc-modal__image-badge';
        badge.textContent = `Image ${String(index + 1).padStart(2, '0')}`;
        wrap.append(badge);

        const img = document.createElement('img');
        img.className = 'mmc-modal__image';
        img.src = image.dataset.preview;
        img.alt = image.dataset.alt || `${data.dataset.productTitle} image ${index + 1}`;
        img.loading = index > 2 ? 'lazy' : 'eager';
        wrap.append(img);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mmc-modal__download';
        button.dataset.mmcDownload = '';
        button.dataset.url = image.dataset.download;
        button.dataset.filename = image.dataset.filename;
        button.innerHTML = '<span class="mmc-modal__download-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h6l2 2h10v10.5H3z"></path><path d="M12 11v5m0 0-2-2m2 2 2-2"></path></svg></span><span>Download original image</span>';

        item.append(wrap, button);
        imageGrid.append(item);
      });

      document.body.classList.add('mmc-modal-open');
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function') this.modal.close();
      else this.modal.removeAttribute('open');
      document.body.classList.remove('mmc-modal-open');
    }

    async downloadImage(button) {
      const originalLabel = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Preparing download…';

      try {
        const response = await fetch(button.dataset.url);
        if (!response.ok) throw new Error(`Download failed (${response.status})`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = button.dataset.filename || 'product-image.jpg';
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (error) {
        console.error('[Multimedia Centre]', error);
        window.open(button.dataset.url, '_blank', 'noopener');
      } finally {
        button.disabled = false;
        button.innerHTML = originalLabel;
      }
    }

    safeFilename(value) {
      return value
        .toString()
        .trim()
        .replace(/[^a-z0-9._-]+/gi, '_')
        .replace(/^_+|_+$/g, '') || 'product-images';
    }

    async downloadAllImages(button) {
      const status = this.modal?.querySelector('[data-mmc-download-status]');
      const imageButtons = [...this.modal.querySelectorAll('[data-mmc-download]')];
      if (!imageButtons.length || button.disabled) return;

      button.disabled = true;
      button.textContent = 'Preparing ZIP…';

      try {
        const files = [];
        for (let index = 0; index < imageButtons.length; index += 1) {
          const imageButton = imageButtons[index];
          if (status) status.textContent = `Downloading image ${index + 1} of ${imageButtons.length}…`;

          const response = await fetch(imageButton.dataset.url);
          if (!response.ok) throw new Error(`Image ${index + 1} failed (${response.status})`);
          files.push({
            name: this.safeFilename(imageButton.dataset.filename || `image-${index + 1}.jpg`),
            data: new Uint8Array(await response.arrayBuffer())
          });
        }

        if (status) status.textContent = 'Creating ZIP file…';
        const zipBlob = this.createStoredZip(files);
        const objectUrl = URL.createObjectURL(zipBlob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `${this.modal.dataset.zipName || 'product-images'}.zip`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
        if (status) status.textContent = `${files.length} images downloaded.`;
      } catch (error) {
        console.error('[Multimedia Centre]', error);
        if (status) status.textContent = 'The ZIP could not be created. Please download the images individually.';
      } finally {
        button.disabled = false;
        button.textContent = 'Download all images (.zip)';
      }
    }

    async downloadSelectedProducts(button) {
      if (!this.selectedProducts.size || button.disabled) return;
      const status = button.closest('[data-mmc-custom-filters]')?.querySelector('[data-mmc-filtered-download-status]')
        || button.closest('[data-mmc-collection-download]')?.querySelector('[data-mmc-collection-status]')
        || this.root.querySelector('[data-mmc-selection-status]');
      const originalLabel = button.innerHTML;
      const products = [...this.selectedProducts.values()];
      const images = products.flatMap((product) => product.images.map((image) => ({ ...image, product })));

      button.disabled = true;
      button.textContent = 'Preparing ZIP…';

      try {
        const files = [];
        const usedNames = new Map();

        for (let index = 0; index < images.length; index += 1) {
          const image = images[index];
          if (status) status.textContent = `Downloading image ${index + 1} of ${images.length}…`;
          const response = await fetch(image.url);
          if (!response.ok) throw new Error(`Image ${index + 1} failed (${response.status})`);

          let filename = this.safeFilename(image.filename || `${image.product.code}-${index + 1}.jpg`);
          const nameKey = filename.toLowerCase();
          const duplicateNumber = (usedNames.get(nameKey) || 0) + 1;
          usedNames.set(nameKey, duplicateNumber);
          if (duplicateNumber > 1) filename = this.numberedFilename(filename, duplicateNumber);

          files.push({
            name: filename,
            data: new Uint8Array(await response.arrayBuffer())
          });
        }

        if (status) status.textContent = 'Creating ZIP file…';
        const zipBlob = this.createStoredZip(files);
        const objectUrl = URL.createObjectURL(zipBlob);
        const anchor = document.createElement('a');
        const date = new Date().toISOString().slice(0, 10);
        anchor.href = objectUrl;
        anchor.download = button.dataset.archiveName || `mmc-selected-products-${date}.zip`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
        if (status) status.textContent = `${files.length} images downloaded.`;
      } catch (error) {
        console.error('[Multimedia Centre]', error);
        if (status) status.textContent = 'The ZIP could not be created. Please reduce the selection and try again.';
      } finally {
        button.disabled = false;
        button.innerHTML = originalLabel;
      }
    }

    async prepareFilteredDownload(button) {
      if (!this.filteredCards.length || button.disabled) return;
      const status = this.customFilters?.querySelector('[data-mmc-filtered-download-status]');
      await this.prepareProductSetDownload(
        button,
        this.filteredCards,
        status,
        'Filtered product download',
        `mmc-filtered-products-${new Date().toISOString().slice(0, 10)}.zip`
      );
    }

    async prepareProductSetDownload(button, cards, status, eyebrow, archiveName) {
      if (!cards.length || button.disabled) return;
      const originalLabel = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Checking images…';
      try {
        if (this.productProvider?.hydrateCards) {
          await this.productProvider.hydrateCards(cards, (loaded, total) => {
            if (status) status.textContent = `Checking product images ${loaded} of ${total}…`;
          });
        }
        const products = cards.map((card) => this.readProductDownloadData(card)).filter(Boolean);
        const imageCount = products.reduce((total, product) => total + product.images.length, 0);
        const seconds = Math.max(10, Math.ceil(imageCount * 0.8));
        const estimate = this.formatEstimatedTime(seconds);
        const dialog = this.root.querySelector('[data-mmc-collection-confirm]');
        dialog.querySelector('[data-mmc-confirm-eyebrow]').textContent = eyebrow;
        dialog.querySelector('[data-mmc-confirm-title]').textContent = 'Please keep this tab open';
        dialog.querySelector('[data-mmc-confirm-message]').textContent = `${products.length} products and ${imageCount} images will be downloaded in one ZIP file. Estimated processing time: ${estimate}. Please do not close this tab or navigate away.`;
        this.pendingFilteredProducts = products;
        this.pendingFilteredButton = button;
        this.pendingFilteredArchiveName = archiveName;
        if (status) status.textContent = '';
        if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      } catch (error) {
        console.error('[Multimedia Centre]', error);
        if (status) status.textContent = 'The filtered image list could not be prepared. Please try again.';
        button.disabled = false;
      } finally {
        button.innerHTML = originalLabel;
      }
    }

    formatEstimatedTime(seconds) {
      let minutes = Math.max(1, Math.ceil(seconds / 60));
      const days = Math.floor(minutes / 1440);
      minutes -= days * 1440;
      const hours = Math.floor(minutes / 60);
      minutes -= hours * 60;
      const parts = [];
      if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
      if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
      if (minutes || !parts.length) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
      return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts[0];
    }

    async downloadFilteredProducts(button, products, archiveName) {
      const originalSelection = this.selectedProducts;
      this.selectedProducts = new Map(products.map((product) => [product.key, product]));
      button.dataset.archiveName = archiveName || `mmc-filtered-products-${new Date().toISOString().slice(0, 10)}.zip`;
      button.disabled = false;
      try { await this.downloadSelectedProducts(button); }
      finally {
        this.selectedProducts = originalSelection;
        delete button.dataset.archiveName;
        this.updateSelectionBar();
      }
    }

    numberedFilename(filename, number) {
      const lastDot = filename.lastIndexOf('.');
      if (lastDot <= 0) return `${filename}_${number}`;
      return `${filename.slice(0, lastDot)}_${number}${filename.slice(lastDot)}`;
    }

    openPreparedDownloadConfirmation(button) {
      const url = button?.dataset.downloadUrl;
      if (!url) return;
      const label = button.dataset.downloadLabel || 'Image library';
      const dialog = this.root.querySelector('[data-mmc-collection-confirm]');
      if (!dialog) {
        this.startPreparedDownload({ url, label });
        return;
      }

      dialog.querySelector('[data-mmc-confirm-eyebrow]').textContent = `${label} download`;
      dialog.querySelector('[data-mmc-confirm-title]').textContent = 'Please keep this tab open';
      dialog.querySelector('[data-mmc-confirm-message]').textContent = 'This prepared ZIP file may be large and could take a while to download. Please do not close this tab until the download has started.';
      this.pendingDirectDownload = { url, label };
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    startPreparedDownload(download) {
      if (!download?.url) return;
      const anchor = document.createElement('a');
      anchor.href = download.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.download = '';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    }

    closeCollectionDownloadConfirmation() {
      const dialog = this.root.querySelector('[data-mmc-collection-confirm]');
      if (!dialog) return;
      if (this.pendingFilteredButton) this.pendingFilteredButton.disabled = false;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    }

    createStoredZip(files) {
      const encoder = new TextEncoder();
      const localParts = [];
      const centralParts = [];
      let offset = 0;
      const now = new Date();
      const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
      const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();

      files.forEach((file) => {
        const name = encoder.encode(file.name);
        const crc = this.crc32(file.data);
        const localHeader = new Uint8Array(30 + name.length);
        const localView = new DataView(localHeader.buffer);
        localView.setUint32(0, 0x04034b50, true);
        localView.setUint16(4, 20, true);
        localView.setUint16(6, 0x0800, true);
        localView.setUint16(8, 0, true);
        localView.setUint16(10, dosTime, true);
        localView.setUint16(12, dosDate, true);
        localView.setUint32(14, crc, true);
        localView.setUint32(18, file.data.length, true);
        localView.setUint32(22, file.data.length, true);
        localView.setUint16(26, name.length, true);
        localView.setUint16(28, 0, true);
        localHeader.set(name, 30);
        localParts.push(localHeader, file.data);

        const centralHeader = new Uint8Array(46 + name.length);
        const centralView = new DataView(centralHeader.buffer);
        centralView.setUint32(0, 0x02014b50, true);
        centralView.setUint16(4, 20, true);
        centralView.setUint16(6, 20, true);
        centralView.setUint16(8, 0x0800, true);
        centralView.setUint16(10, 0, true);
        centralView.setUint16(12, dosTime, true);
        centralView.setUint16(14, dosDate, true);
        centralView.setUint32(16, crc, true);
        centralView.setUint32(20, file.data.length, true);
        centralView.setUint32(24, file.data.length, true);
        centralView.setUint16(28, name.length, true);
        centralView.setUint16(30, 0, true);
        centralView.setUint16(32, 0, true);
        centralView.setUint16(34, 0, true);
        centralView.setUint16(36, 0, true);
        centralView.setUint32(38, 0, true);
        centralView.setUint32(42, offset, true);
        centralHeader.set(name, 46);
        centralParts.push(centralHeader);
        offset += localHeader.length + file.data.length;
      });

      const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
      const end = new Uint8Array(22);
      const endView = new DataView(end.buffer);
      endView.setUint32(0, 0x06054b50, true);
      endView.setUint16(4, 0, true);
      endView.setUint16(6, 0, true);
      endView.setUint16(8, files.length, true);
      endView.setUint16(10, files.length, true);
      endView.setUint32(12, centralSize, true);
      endView.setUint32(16, offset, true);
      endView.setUint16(20, 0, true);

      return new Blob([...localParts, ...centralParts, end], { type: 'application/zip' });
    }

    crc32(bytes) {
      let crc = 0xffffffff;
      for (let index = 0; index < bytes.length; index += 1) {
        crc ^= bytes[index];
        for (let bit = 0; bit < 8; bit += 1) {
          crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
        }
      }
      return (crc ^ 0xffffffff) >>> 0;
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-mmc]').forEach((root) => {
      if (root.dataset.mmcInitialised === 'true') return;
      root.dataset.mmcInitialised = 'true';
      root.mmcInstance = new MultimediaCentre(root);
    });
  };

  window.MMCInitialise = initialise;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
